/** Utilities for filtering tasks based on view requirements (overdue, upcoming, unscheduled). */
import { isSchedulableTask } from '@/features/tasks/utils/scheduling/taskSchedulingUtils';

export const filterOverdueTasks = (
  tasks,
  isSmallScreen,
  tasksMap = new Map(),
) => {
  const now = new Date();
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);

  if (isSmallScreen) {
    return tasks.filter((task) => {
      if (
        task.completed ||
        task.status === 'Backlog' ||
        !task.date ||
        !task.time
      )
        return false;
      if (!isSchedulableTask(task, tasksMap)) return false;

      const taskDate = task.date.toDate
        ? task.date.toDate()
        : new Date(task.date);
      if (isNaN(taskDate.getTime())) return false;

      const [hours, minutes] = task.time.split(':').map(Number);
      taskDate.setHours(hours, minutes, 0, 0);

      const endTime = new Date(
        taskDate.getTime() + (task.duration || 0) * 60 * 1000,
      );
      return endTime < oneHourAgo;
    });
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return tasks.filter((task) => {
    if (task.completed || task.status === 'Backlog' || !task.date) return false;
    if (!isSchedulableTask(task, tasksMap)) return false;
    const taskDate = task.date.toDate
      ? task.date.toDate()
      : new Date(task.date);
    if (isNaN(taskDate.getTime())) return false;
    taskDate.setHours(0, 0, 0, 0);
    return taskDate < today;
  });
};

export const filterUpcomingTasks = (tasks, tasksMap = new Map()) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const limitDate = new Date(today);
  limitDate.setDate(limitDate.getDate() + 3);
  return tasks.filter((task) => {
    if (
      task.completed ||
      task.status === 'Backlog' ||
      !task.date ||
      task.time ||
      task.person
    )
      return false;
    if (!isSchedulableTask(task, tasksMap)) return false;
    const taskDate = task.date.toDate
      ? task.date.toDate()
      : new Date(task.date);
    if (isNaN(taskDate.getTime())) return false;
    taskDate.setHours(0, 0, 0, 0);
    return taskDate >= today && taskDate < limitDate;
  });
};

export const isUnscheduledCandidate = (task) =>
  !task.date && !task.completed && task.status !== 'Backlog';

/**
 * @param {import('@/types').Task} task
 * @returns {string|null}
 */
const getDescendantPathPrefix = (task) => {
  if (!task?.materializedPath) return null;
  const path = task.materializedPath;
  return path.endsWith('.') ? path : `${path}.`;
};

/**
 * @param {import('@/types').Task} candidate
 * @param {import('@/types').Task} ancestor
 * @returns {boolean}
 */
const isDescendantOf = (candidate, ancestor) => {
  if (!candidate || !ancestor?.id || candidate.id === ancestor.id) return false;
  if (candidate.parentId === ancestor.id) return true;
  const prefix = getDescendantPathPrefix(ancestor);
  return Boolean(prefix && candidate.materializedPath?.startsWith(prefix));
};

/**
 * Open leaf subtasks under a parent. Completed/backlog are excluded from the
 * denominator. "Scheduled" reuses `isUnscheduledCandidate` (has a date).
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {{ scheduled: number, total: number } | null}
 */
export const getOpenLeafScheduleProgress = (task, tasksMap = new Map()) => {
  if (!task?.id || !tasksMap?.size) return null;

  const leaves = [];
  for (const candidate of tasksMap.values()) {
    if (!isDescendantOf(candidate, task)) continue;
    if (!isSchedulableTask(candidate, tasksMap)) continue;
    leaves.push(candidate);
  }

  if (leaves.length === 0) return null;

  const scheduled = leaves.filter(
    (leaf) => !isUnscheduledCandidate(leaf),
  ).length;
  return { scheduled, total: leaves.length };
};

/**
 * True when every open leaf under the parent is already scheduled — parent
 * should leave Te plannen.
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 */
export const isFullyScheduledParent = (task, tasksMap = new Map()) => {
  const progress = getOpenLeafScheduleProgress(task, tasksMap);
  return Boolean(progress && progress.scheduled === progress.total);
};

/**
 * Partial progress for Te plannen meta + tint (0 < scheduled < total).
 * @param {import('@/types').Task} task
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {{ scheduled: number, total: number } | null}
 */
export const getPartiallyScheduledProgress = (task, tasksMap = new Map()) => {
  const progress = getOpenLeafScheduleProgress(task, tasksMap);
  if (!progress || progress.scheduled <= 0 || progress.scheduled >= progress.total) {
    return null;
  }
  return progress;
};

/**
 * Builds the Unscheduled rail list: unscheduled tasks plus ancestor headers
 * so hoofdtaken and ingesprongen subtaken stay visible as one structure.
 * Parents whose open leaf subtasks are all scheduled are omitted.
 * @param {import('@/types').Task[]} tasks
 * @param {Map<string, import('@/types').Task>} tasksMap
 * @returns {import('@/types').Task[]}
 */
export const buildUnscheduledTaskList = (tasks, tasksMap = new Map()) => {
  const map = tasksMap.size
    ? tasksMap
    : new Map((tasks ?? []).map((task) => [task.id, task]));
  const included = new Map();

  const includeWithAncestors = (task) => {
    let current = task;
    while (current) {
      if (current.completed || current.status === 'Backlog') break;
      included.set(current.id, current);
      current = current.parentId ? map.get(current.parentId) : null;
    }
  };

  (tasks ?? []).forEach((task) => {
    if (isUnscheduledCandidate(task)) {
      includeWithAncestors(task);
    }
  });

  return Array.from(included.values())
    .filter((task) => !isFullyScheduledParent(task, map))
    .sort((a, b) => {
      const pathA = a.materializedPath || '';
      const pathB = b.materializedPath || '';
      if (pathA < pathB) return -1;
      if (pathA > pathB) return 1;
      return 0;
    });
};

export const filterUnscheduledTasks = (tasks, tasksMap = new Map()) =>
  buildUnscheduledTaskList(tasks, tasksMap);
